import React from 'react';
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export type LinkVariant = 'prominent' | 'neutral' | 'discreet' | 'primary' | 'secondary' | 'dark';
export type LinkSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface LinkProps
  extends Omit<MuiLinkProps, 'variant' | 'size' | keyof PounceStyleProps>, PounceStyleProps {
  /** Whether the link is external and should open in a new tab */
  external?: boolean;

  /** The text style & color of the link */
  variant?: LinkVariant;

  /** The size of the link */
  size?: LinkSize;

  /** Whether the link should be disabled */
  disabled?: boolean;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiLinkProps['sx'];

  /** The path to navigate to - can be a string or react-router location object */
  to?: string | { pathname?: string; search?: string; hash?: string; state?: unknown };

  /** The element to render the link as */
  as?: React.ElementType;
}

/**
 * Link component built on top of MUI Link.
 *
 * Use Link whenever you want to add a link to another internal or external page.
 * Supports all pounce style props and maintains the same API as the original Pounce Link.
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { external, disabled, variant = 'primary', size = 'large', as, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  const externalProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};

  // Combine pounce styles with user sx using deepmerge for nested objects
  const combinedSx = deepmerge(pounceStyles, sx || {}) as MuiLinkProps['sx'];

  return (
    <MuiLink
      ref={ref}
      component={as}
      data-variant={variant}
      data-size={size}
      aria-disabled={disabled}
      {...externalProps}
      sx={combinedSx}
      {...otherProps}
    />
  );
});
